import createIntlWithLocale from 'src/internal/createIntlWithLocale';
import {handleReactIntlError} from 'src/internal/errors/handleReactIntlError/handleReactIntlError';
import mergeOptions from 'src/internal/mergeOptions';
import fixNegativeZero from 'src/internal/number-formatter/fixNegativeZero';
import fixRounding from 'src/internal/number-formatter/fixRounding';

import type {IAnalytics, IReports} from '@sail/observability';
import type {
  FormatPercentOptions,
  FormatPercentOptionsDefaults,
  FormatPercentOptionsWithDefaults,
} from 'src/public/formatPercent/types';
import type {IntlConfig} from 'src/types';

/**
 * Format a number as a percentage and returns a string with a
 * locale-sensitive representation of the given number.
 *
 * @example Basic {{include "./examples/formatPercent.basic.tsx"}}
 *
 * @see https://sail.stripe.me/apis/intl/useIntl#format-percent
 */
export default function formatPercent(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  analytics: IAnalytics,
  /** The number to be formatted, must be a floating-point number between 0 and 1. */
  number: number,
  /** An object with options to customize the formatting. */
  options?: FormatPercentOptions,
): string {
  const defaults: FormatPercentOptionsDefaults = {
    locale: config.locale,
    maximumFractionDigits: 0,
    signDisplay: 'auto',
  };

  const optionsWithDefaults: FormatPercentOptionsWithDefaults = mergeOptions(
    config,
    defaults,
    reports,
    options,
    {
      style: 'percent',
    },
  );

  let consolidatedNumber = number;
  consolidatedNumber = fixNegativeZero(number);
  consolidatedNumber = fixRounding(
    consolidatedNumber,
    2 + optionsWithDefaults.maximumFractionDigits,
    optionsWithDefaults.roundingMode,
  );

  const intl = createIntlWithLocale(
    optionsWithDefaults.locale,
    config,
    reports,
    analytics,
  );

  try {
    return intl.formatNumber(consolidatedNumber, optionsWithDefaults);
  } catch (error) {
    handleReactIntlError(reports, analytics, config.locale, error);

    return '';
  }
}

export type FormatPercent = typeof formatPercent;

export type FormatPercentDeclarative = (
  number: Parameters<FormatPercent>[3],
  options?: Parameters<FormatPercent>[4],
) => ReturnType<FormatPercent>;
